/**
 * useResolveSourcePath — builds a `resolveLabel` function that surfaces can
 * pass to `DataMappingViewerV2` / `DataUsageList` so dataUsage items render
 * with human-readable breadcrumb chains instead of raw Firebase IDs.
 *
 * Loads source data for the project via the same hook strategy-blocks uses
 * (`useSourceDataLoader`), normalises `actorsByAudience` back onto each
 * audience, and returns a stable callback. While the data is still loading
 * the resolver returns null for every path — callers fall back to showing
 * the monospace sourcePath, which matches pre-Sprint-17 behaviour.
 */

import { useMemo } from 'react';
import { useSourceDataLoader } from '@modules/strategy-blocks/hooks/useSourceDataLoader';
import { resolveSourcePath, formatResolvedPath } from '../utils/resolveSourcePath';
import type { HydratedSourceData } from '@modules/shared-types';

export interface UseResolveSourcePathResult {
  /** Ready-to-pass resolver. Safe to call before data loads — returns null. */
  resolveLabel: (sourcePath: string) => string | null;
  /** Raw loading + error state for callers that want to show skeletons. */
  loading: boolean;
  error: Error | null;
}

export function useResolveSourcePath(projectId: string): UseResolveSourcePathResult {
  const { data, loading, error } = useSourceDataLoader({ projectId, autoLoad: true });

  // Rebuild `HydratedSourceData` shape: the loader returns actors keyed by
  // audience id, but `resolveSourcePath` walks `audience.actors[]` inline.
  const source: HydratedSourceData | null = useMemo(() => {
    if (!data || data.audiences.length === 0) return null;
    // The loader returns loader-specific entity types that overlap structurally
    // with HydratedSourceData's loose `[key: string]: unknown` index signatures;
    // the double cast keeps TS happy without widening the loader's types.
    return {
      audiences: data.audiences.map((a: any) => ({
        ...a,
        actors: a.id ? (data.actorsByAudience[a.id] ?? []) : [],
      })),
      tasks: data.tasks,
      studyCases: data.studyCases,
      reviews: data.reviews,
      ratings: data.ratings,
      bigNumbers: data.bigNumbers,
      // Insights aren't loaded by useSourceDataLoader today; leave undefined so
      // `@insight[...]` paths fall through to raw display.
      insights: undefined,
    } as unknown as HydratedSourceData;
  }, [data]);

  const resolveLabel = useMemo(() => {
    return (sourcePath: string): string | null => {
      if (!source) return null;
      return formatResolvedPath(sourcePath, source);
    };
  }, [source]);

  return { resolveLabel, loading, error };
}

export { resolveSourcePath };
